/**
 * Api_Endpoints.ts
 * ──────────────────────────────────────────────────────────────────────────
 * SINGLE SOURCE OF TRUTH for every backend URL used in SK Hire.
 *
 * Two backend services:
 *   PLATFORM_API  → skplatform-api  (identity & session — the shared
 *                   *.softknack.com httpOnly cookie; SK Hire owns NO auth UI,
 *                   it only probes /me and posts logout/refresh)
 *   HIRE_API      → sk-hire-api     (settings, pipelines, requirements,
 *                                    candidates, submissions, matching,
 *                                    attachments, board integration)
 *
 * Authentication (login / signup / forgot / reset / verify) is owned entirely
 * by the SK Platform. When there is no valid session, the app hands off to the
 * platform auth app (see navigation/platformAuthRedirect.ts) — it never renders
 * an auth screen. The only platform calls below are the session-plumbing the
 * product legitimately needs: /me, /auth/refresh (silent), /auth/logout.
 *
 * Rules:
 *   - Zero hardcoded URLs anywhere else in the codebase.
 *   - To switch environments, update ONLY the base URLs below.
 * ──────────────────────────────────────────────────────────────────────────
 */

// Resolve a build-time base URL. webpack's DefinePlugin text-replaces the
// `process.env.X` literal in `read()` at build time; if it didn't (e.g. a stale
// dev bundle) `process` is undefined in the browser and the read throws — we
// catch that and use the localhost fallback so the app never white-screens.
function apiBase(read: () => string | undefined, fallback: string): string {
  try {
    return read() || fallback;
  } catch {
    return fallback;
  }
}

// ── PLATFORM API base (identity & session — called directly) ───────────────
// Injected at build time via webpack DefinePlugin (PLATFORM_API_URL env var).
// Local dev: skplatform-api runs on :3013.
export const PLATFORM_API = apiBase(() => process.env.PLATFORM_API_URL, 'http://localhost:3013');

// ── HIRE API base (requirements, candidates, matching, etc.) ──────────────
// Injected at build time via webpack DefinePlugin (HIRE_API_URL env var).
// Local dev: sk-hire-api runs on :3012.
export const HIRE_API = apiBase(() => process.env.HIRE_API_URL, 'http://localhost:3012');

// ── Versioned / namespaced prefixes ───────────────────────────────────────
const P = `${PLATFORM_API}/v1`;        // Identity/session — skplatform-api v1 (direct)
const H = `${HIRE_API}/api/hire`;      // Hire — mounted at /api/hire/* (see sk-hire-api app.ts)
const HM = `${HIRE_API}/api/hire/v2`;  // Hire matching-engine v2 (SKHIRE-SCHEMA-001 rev 1.1)

/* ═══════════════════════════════════════════════════════════════════════════
   HIRE · Matching engine — Requisitions ("Jobs")
═══════════════════════════════════════════════════════════════════════════ */

export const HireMatch_Requisitions_Url = `${HM}/requisitions`;                                  // GET list | POST create
export const HireMatch_Requisition_Extract_Url = `${HM}/requisitions/extract`;                   // POST paste-JD → draft
export const HireMatch_Requisition_ExtractFile_Url = `${HM}/requisitions/extract-file`;          // POST upload (PDF/CSV/Excel) → parse+extract+store
export const HireMatch_Requisition_SourceFile_Url = (id: string) => `${HM}/requisitions/${id}/source-file`; // GET signed URL for stored original
export const HireMatch_Requisition_SourceFileDownload_Url = (id: string) => `${HM}/requisitions/${id}/source-file/download`; // GET bytes as an attachment
export const HireMatch_Requisition_Url = (id: string) => `${HM}/requisitions/${id}`;             // GET | PATCH (append revision)
export const HireMatch_Requisition_Activate_Url = (id: string) => `${HM}/requisitions/${id}/activate`; // POST
export const HireMatch_Requisition_Close_Url = (id: string) => `${HM}/requisitions/${id}/close`;       // POST — archive (status→closed)
export const HireMatch_Requisition_Reopen_Url = (id: string) => `${HM}/requisitions/${id}/reopen`;     // POST — closed→active (undo an auto-close)

/* ═══════════════════════════════════════════════════════════════════════════
   HIRE · Matching engine — Candidates
═══════════════════════════════════════════════════════════════════════════ */

export const HireMatch_Candidates_Url = `${HM}/candidates`;                                     // GET list | POST create
export const HireMatch_Candidate_Lookup_Url = `${HM}/candidates/lookup`;                        // GET ?email=&phone= → is this contact already on file?
export const HireMatch_Candidate_Url = (id: string) => `${HM}/candidates/${id}`;                // GET
export const HireMatch_Candidate_ExtractFile_Url = `${HM}/candidates/extract-file`;             // POST upload resume → parse+extract+store
export const HireMatch_Candidate_ExtractText_Url = `${HM}/candidates/extract-text`;             // POST pasted resume text → scrape name/email/phone
export const HireMatch_Candidate_SourceFile_Url = (id: string) => `${HM}/candidates/${id}/source-file`; // GET signed URL for stored resume
export const HireMatch_Candidate_SourceFileDownload_Url = (id: string) => `${HM}/candidates/${id}/source-file/download`; // GET bytes as an attachment
export const HireMatch_Candidate_Profile_Url = (id: string) => `${HM}/candidates/${id}/profile`; // PATCH edit the current extracted profile in place
export const HireMatch_Candidate_Resumes_Url = (id: string) => `${HM}/candidates/${id}/resumes`; // POST new version
export const HireMatch_Candidate_Consent_Url = (id: string) => `${HM}/candidates/${id}/consent`;  // POST renew|withdraw
export const HireMatch_Candidate_JobMatches_Url = (id: string) => `${HM}/candidates/${id}/job-matches`; // GET reverse match
export const HireMatch_Candidate_Actions_Url = (id: string) => `${HM}/candidates/${id}/actions`; // GET history
export const HireMatch_Candidate_DismissDuplicate_Url = (id: string) => `${HM}/candidates/${id}/dismiss-duplicate`; // POST clear advisory flag

/* ═══════════════════════════════════════════════════════════════════════════
   HIRE · Matching engine — Match funnel · actions / kanban
═══════════════════════════════════════════════════════════════════════════ */

export const HireMatch_Requisition_MatchRuns_Url = (id: string) => `${HM}/requisitions/${id}/match-runs`; // GET list | POST run
export const HireMatch_Requisition_LatestRun_Url = (id: string) => `${HM}/requisitions/${id}/match-runs/latest`; // GET newest run + staleness (no re-run)
export const HireMatch_MatchRun_Url = (id: string) => `${HM}/match-runs/${id}`;                          // GET run detail
export const HireMatch_MatchRun_Feedback_Url = (id: string) => `${HM}/match-runs/${id}/feedback`;        // GET my verdicts | POST verdict
export const HireMatch_MatchRun_FeedbackItem_Url = (id: string, candidateId: string) => `${HM}/match-runs/${id}/feedback/${candidateId}`; // DELETE undo
export const HireMatch_MatchRun_GoodMatch_Url = (id: string) => `${HM}/match-runs/${id}/good-match`;      // POST {candidateId}
export const HireMatch_MatchRun_FalsePositive_Url = (id: string) => `${HM}/match-runs/${id}/false-positive`; // POST {candidateId}
export const HireMatch_Requisition_Pipeline_Url = (id: string) => `${HM}/requisitions/${id}/pipeline`;   // GET kanban projection
export const HireMatch_Exclusion_Url = (reqId: string, candidateId: string) => `${HM}/requisitions/${reqId}/candidates/${candidateId}/exclusion`; // GET why-not-matching
export const HireMatch_Actions_Url = `${HM}/actions`;                                                    // POST record action

/* ── HIRE · Matching engine — Ops console (Softknack-internal; hire:admin) ── */
export const HireMatch_Ops_Metrics_Url = `${HM}/ops/metrics`;
export const HireMatch_Ops_FeedbackAnalytics_Url = `${HM}/ops/feedback-analytics`;
export const HireMatch_Ops_CurationQueue_Url = `${HM}/ops/curation-queue`;
export const HireMatch_Ops_Skills_Url = `${HM}/ops/skills`;
export const HireMatch_Ops_CurationPromote_Url = (id: string) => `${HM}/ops/curation/${id}/promote`;
export const HireMatch_Ops_CurationAttach_Url = (id: string) => `${HM}/ops/curation/${id}/attach`;
export const HireMatch_Ops_CurationDiscard_Url = (id: string) => `${HM}/ops/curation/${id}/discard`;
export const HireMatch_Ops_Reextract_Url = `${HM}/ops/reextract`;
export const HireMatch_Ops_TitleQueue_Url = `${HM}/ops/title-curation-queue`;
export const HireMatch_Ops_TitlePromote_Url = (id: string) => `${HM}/ops/title-curation/${id}/promote`;
export const HireMatch_Ops_TitleDiscard_Url = (id: string) => `${HM}/ops/title-curation/${id}/discard`;

/* ═══════════════════════════════════════════════════════════════════════════
   HIRE · Matching engine — Compliance (DPDP) · Integrations · Dashboard
═══════════════════════════════════════════════════════════════════════════ */

export const HireMatch_Consent_Stats_Url = `${HM}/compliance/consent-stats`;
export const HireMatch_Consent_Expiring_Url = `${HM}/compliance/expiring`;
export const HireMatch_Consent_Events_Url = `${HM}/compliance/consent-events`;
export const HireMatch_Erasures_Url = `${HM}/compliance/erasures`;
export const HireMatch_Candidate_Erasure_Url = (id: string) => `${HM}/candidates/${id}/erasure`;

export const HireMatch_Portals_Url = `${HM}/portals`;
export const HireMatch_Portal_Connect_Url = (slug: string) => `${HM}/portals/${slug}/connect`;
export const HireMatch_Portal_Disconnect_Url = (slug: string) => `${HM}/portals/${slug}/disconnect`;
export const HireMatch_Imports_Url = `${HM}/imports`;
export const HireMatch_Import_Url = (id: string) => `${HM}/imports/${id}`;

export const HireMatch_Dashboard_Url = `${HM}/dashboard`;
export const HireMatch_Credits_Balance_Url = `${HM}/credits/balance`;
export const HireMatch_Workspace_Url = `${HM}/workspace`;               // GET snapshot (counts + AI usage)
export const HireMatch_Workspace_Stream_Url = `${HM}/workspace/stream`; // GET SSE (live counts + tokens)
export const HireMatch_Ai_Usage_Url = `${HM}/ai/usage`;                 // GET detailed usage (summary + breakdowns + series + log)
export const HireMatch_Requisition_Token_History_Url = (id: string) => `${HM}/requisitions/${id}/token-history`; // GET per-job match-run token history

/* ═══════════════════════════════════════════════════════════════════════════
   PLATFORM — Session plumbing only (shared httpOnly cookie).
   Auth UI (login/signup/forgot/reset/verify) lives on the SK Platform app.
═══════════════════════════════════════════════════════════════════════════ */

export const Platform_Me_Url = `${P}/me`;                // GET  — identity probe (200 = logged in, 401 = not)
export const Platform_Refresh_Token_Url = `${P}/auth/refresh`;  // POST — silent rotation (httpOnly cookie)
export const Platform_Logout_Url = `${P}/auth/logout`;   // POST — clears session cookies (204)

/* ═══════════════════════════════════════════════════════════════════════════
   HIRE API — Settings  (tenant config: agency mode, pull switches, retention)
═══════════════════════════════════════════════════════════════════════════ */

export const Hire_Settings_Url = `${H}/settings`;                  // GET | PUT { isAgency?, pullCandidates?, pullOpenings?, retentionDays? }

/* ═══════════════════════════════════════════════════════════════════════════
   HIRE API — Pipelines, Stages & Outcome reasons
═══════════════════════════════════════════════════════════════════════════ */

export const Hire_Pipelines_Url = `${H}/pipelines`;                                          // GET | POST { name }
export const Hire_Pipeline_Url = (pipelineId: string) => `${H}/pipelines/${pipelineId}`;    // GET
export const Hire_Pipeline_Stages_Url = (pipelineId: string) => `${H}/pipelines/${pipelineId}/stages`;          // POST { label } — add middle stage
export const Hire_Pipeline_Stages_Reorder_Url = (pipelineId: string) => `${H}/pipelines/${pipelineId}/stages/reorder`; // PATCH { orderedStageIds }
export const Hire_Stage_Url = (stageId: string) => `${H}/pipelines/stages/${stageId}`;       // PATCH { label } | DELETE
export const Hire_OutcomeReasons_Url = `${H}/pipelines/outcome-reasons/list`;                // GET — all reasons (rejected/withdrawn)
export const Hire_OutcomeReason_Create_Url = `${H}/pipelines/outcome-reasons`;               // POST { category, label }
export const Hire_OutcomeReason_Url = (reasonId: string) => `${H}/pipelines/outcome-reasons/${reasonId}`; // DELETE

/* ═══════════════════════════════════════════════════════════════════════════
   HIRE API — Requirements (openings) + polymorphic owners (agency)
═══════════════════════════════════════════════════════════════════════════ */

export const Hire_Requirements_Url = `${H}/requirements`;                                   // GET (raw list) | POST (create)
export const Hire_Requirements_Search_Url = `${H}/requirements/search`;                     // GET ?q&status&ownerType&view&sort&order&page&pageSize → { data, meta }
export const Hire_Requirements_Stats_Url = `${H}/requirements/stats`;                        // GET → KPI strip + per-view tab counts
export const Hire_Requirement_Url = (id: string) => `${H}/requirements/${id}`;             // GET | PATCH | DELETE (deactivate)
export const Hire_Requirement_Status_Url = (id: string) => `${H}/requirements/${id}/status`; // PATCH { status }
export const Hire_Owners_Url = `${H}/requirements/owners/list`;                             // GET — recruiter↔client assignments
export const Hire_Owner_Create_Url = `${H}/requirements/owners`;                            // POST { crmAccountId, recruiterUserId }
export const Hire_Owner_Url = (id: string) => `${H}/requirements/owners/${id}`;            // DELETE

/* ═══════════════════════════════════════════════════════════════════════════
   HIRE API — Dashboard (one-call workspace aggregates)
═══════════════════════════════════════════════════════════════════════════ */

export const Hire_Dashboard_Url = `${H}/dashboard`;                                          // GET → counts + funnel + AI usage + recent activity

/* ═══════════════════════════════════════════════════════════════════════════
   HIRE API — Candidates (shared pool) + contacts, merge, erasure
═══════════════════════════════════════════════════════════════════════════ */

export const Hire_Candidates_Url = `${H}/candidates`;                                       // GET (raw list) | POST (create / enrich)
export const Hire_Candidates_Search_Url = `${H}/candidates/search`;                         // GET ?q&skill&source&location&minExperience&tag&keyword&sort&order&page&pageSize → { data, meta }
export const Hire_Candidate_Facets_Url = `${H}/candidates/facets`;                          // GET → { skills, sources, locations }
export const Hire_Candidate_Url = (id: string) => `${H}/candidates/${id}`;                 // GET
export const Hire_Candidate_Contacts_Url = (id: string) => `${H}/candidates/${id}/contacts`; // POST { type, value }
export const Hire_Candidate_Contact_Primary_Url = (id: string, contactId: string) => `${H}/candidates/${id}/contacts/${contactId}/primary`; // PATCH
export const Hire_Candidate_Contact_Url = (id: string, contactId: string) => `${H}/candidates/${id}/contacts/${contactId}`; // DELETE
export const Hire_Candidates_Merge_Url = `${H}/candidates/merge`;                           // POST { survivorId, loserId }
export const Hire_Candidate_Erase_Url = (id: string) => `${H}/candidates/${id}/erase`;     // POST — DPDP erasure (admin)
export const Hire_Candidates_Retention_Due_Url = `${H}/candidates/retention/due`;           // GET (admin)

/* ═══════════════════════════════════════════════════════════════════════════
   HIRE API — Submissions (candidate ↔ requirement) + stage moves, notes, history
═══════════════════════════════════════════════════════════════════════════ */

export const Hire_Submissions_Url = `${H}/submissions`;                                     // POST { candidateId, requirementId } (add to pipeline)
export const Hire_Submission_Url = (id: string) => `${H}/submissions/${id}`;               // GET
export const Hire_Submission_Move_Url = (id: string) => `${H}/submissions/${id}/move`;     // POST { toStageId, reasonId?, idempotency }
export const Hire_Submission_History_Url = (id: string) => `${H}/submissions/${id}/history`; // GET — append-only stage history
export const Hire_Submission_Notes_Url = (id: string) => `${H}/submissions/${id}/notes`;   // GET | POST { body }
export const Hire_Submissions_By_Requirement_Url = (requirementId: string) => `${H}/submissions/by-requirement/${requirementId}`; // GET — pipeline
export const Hire_Submissions_By_Candidate_Url = (candidateId: string) => `${H}/submissions/by-candidate/${candidateId}`;         // GET

/* ═══════════════════════════════════════════════════════════════════════════
   HIRE API — Matching (bidirectional, deterministic engine; LLM rerank optional)
═══════════════════════════════════════════════════════════════════════════ */

export const Hire_Match_Requirement_Candidates_Url = (requirementId: string) => `${H}/matching/requirements/${requirementId}/candidates`; // GET ?limit
export const Hire_Match_Candidate_Openings_Url = (candidateId: string) => `${H}/matching/candidates/${candidateId}/openings`;             // GET ?limit

/* ═══════════════════════════════════════════════════════════════════════════
   HIRE API — Attachments (via SK Drive; scan-state mirrored, never serve unclean)
═══════════════════════════════════════════════════════════════════════════ */

export const Hire_Candidate_Attachments_Url = (candidateId: string) => `${H}/attachments/candidates/${candidateId}`; // GET | POST { filename, ... }
export const Hire_Attachment_SignedUrl_Url = (id: string) => `${H}/attachments/${id}/signed-url`;     // GET — clean files only
export const Hire_Attachment_RefreshScan_Url = (id: string) => `${H}/attachments/${id}/refresh-scan`; // POST
export const Hire_Attachment_Url = (id: string) => `${H}/attachments/${id}`;                          // DELETE

/* ═══════════════════════════════════════════════════════════════════════════
   HIRE API — Board integration (SK Bridge canonical verbs; manual, metered)
═══════════════════════════════════════════════════════════════════════════ */

export const Hire_Integration_Config_Url = `${H}/integration/config`;                       // GET — pull switches + per-board supported verbs
export const Hire_Integration_Accounts_Url = `${H}/integration/accounts`;                   // GET | POST (connect board)
export const Hire_Integration_Account_Url = (id: string) => `${H}/integration/accounts/${id}`; // DELETE (disconnect)
export const Hire_Integration_Publish_Url = `${H}/integration/publish`;                     // POST { requirementId, externalAccountId } — post to board
export const Hire_Integration_ImportCandidate_Url = `${H}/integration/import-candidate`;    // POST { externalAccountId, externalRef }
export const Hire_Integration_ImportOpening_Url = `${H}/integration/import-opening`;        // POST { externalAccountId, externalRef }
export const Hire_Integration_SearchTalent_Url = `${H}/integration/search-talent`;          // POST { externalAccountId, query }
export const Hire_Integration_SearchOpenings_Url = `${H}/integration/search-openings`;      // POST { externalAccountId, query }

/* ═══════════════════════════════════════════════════════════════════════════
   HIRE API — AI usage (metered token ledger; rev2 §8c)
═══════════════════════════════════════════════════════════════════════════ */

export const Hire_AI_Usage_Url = `${H}/ai/usage`;                                           // GET summary+ledger | POST record op
export const Hire_AI_Extract_Keywords_Url = `${H}/ai/extract-keywords`;                     // POST { jdText } → { keywords } (Claude-backed)

/* ═══════════════════════════════════════════════════════════════════════════
   HIRE API — Bulk import (review-before-commit staging; rev2 §8b)
═══════════════════════════════════════════════════════════════════════════ */

export const Hire_Import_Url = `${H}/import`;                                                // POST create batch
export const Hire_Import_Batch_Url = (id: string) => `${H}/import/${id}`;                    // GET batch
export const Hire_Import_Rows_Url = (id: string) => `${H}/import/${id}/rows`;                // POST stage rows · GET list rows
export const Hire_Import_Row_Url = (rowId: string) => `${H}/import/rows/${rowId}`;           // PATCH row decision/fields
export const Hire_Import_Commit_Url = (id: string) => `${H}/import/${id}/commit`;            // POST commit

/* ═══════════════════════════════════════════════════════════════════════════
   HIRE API — Health
═══════════════════════════════════════════════════════════════════════════ */

export const Hire_Health_Url = `${HIRE_API}/healthz`;   // GET — unauthenticated liveness check
